<template>
  <button
    v-if="trimmedPhone"
    type="button"
    :class="button({ variant: 'subtle', size: 'sm', class: props.class })"
    :disabled="isLoading"
    :aria-label="`Call ${trimmedPhone}`"
    @click="handleClick"
  >
    <span class="truncate">{{ trimmedPhone }}</span>
    <Spinner v-if="isLoading" class="size-[1em] shrink-0" />
    <PhoneIcon v-else class="size-[1em] shrink-0" aria-hidden="true" />
  </button>
  <template v-else>n/a</template>
</template>

<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue';
import { Capacitor } from '@capacitor/core';
import { useAbility } from '@casl/vue';
import { Phone as PhoneIcon } from 'lucide-vue-next';
import { button } from '@carvia/theme/lib/cva/button';
import { useDialCall } from '@/queries/use-phone';
import { useMe } from '@/queries/use-users';
import Spinner from '@/components/icons/Spinner.vue';

const props = defineProps<{
  phoneNumber?: string | null;
  class?: HTMLAttributes['class'];
}>();

const ability = useAbility();
const { data: user } = useMe();
const { mutateAsync: dial, isPending: isLoading } = useDialCall();

const trimmedPhone = computed(() => props.phoneNumber?.trim() || '');

const telHref = computed(() =>
  trimmedPhone.value ? `tel:${trimmedPhone.value.replace(/\s/g, '')}` : undefined,
);

const canSip = computed(
  () =>
    !Capacitor.isNativePlatform() &&
    ability.can('ros', 'Phone') &&
    Boolean(user.value?.sipId) &&
    Boolean(trimmedPhone.value),
);

async function handleClick() {
  if (!trimmedPhone.value || !telHref.value) return;

  if (canSip.value) {
    await dial(trimmedPhone.value);
    return;
  }

  window.location.href = telHref.value;
}
</script>
